import React, {useMemo, useEffect, useState, useContext} from 'react';
import Table from "@amzn/awsui-components-react/polaris/table";
import YtdPeriodBalance from 'src/models/periodCharges/YtdPeriodBalance';
import PeriodChargeValidationResult, { PeriodChargeValidationType } from 'src/models/periodCharges/PeriodChargeValidationResult';
import {
    Box,
    SpaceBetween,
    Button,
    Pagination,
    Header,
    StatusIndicator,
    FormField,
    Grid,
    Icon,
    Popover
} from '@amzn/awsui-components-react';
import PeriodChargesLOVsResponse from 'src/models/periodCharges/PeriodChargesLOVsResponse';
import {PeriodChargesContext} from './PeriodChargesView';
import ATPCLIsAutosuggest from '../shared/ATPCLIsAutosuggest';
import {ACTIONS} from 'src/services/period-charges/PeriodChargesReducer';
import StringUtils from 'src/utils/stringUtils';
import {CLI} from 'src/models/common/CLI';
import CONSTANTS from 'src/utils/constants';
import BalanceAdjustmentYtdTopGridModel from "src/models/periodCharges/BalanceAdjustmentYtdTopGridModel";


export default function TopYtdBalancesGrid(props: {
    filterLOVs: PeriodChargesLOVsResponse | null,
    clisAutoSuggestList: string[],
    selectedCLI?: CLI,
    validationResult?: PeriodChargeValidationResult,
    onFilterChanged?: (selectedFilters: any) => void,
    onExportAllCLIs?: () => void,
    onApproveBalance?: () => void,
    onSelectionChanged?: (selectedYtdBalances: any[]) => void,
}) {
    const {
        filterLOVs,
        clisAutoSuggestList,
        selectedCLI,
        validationResult,
        onFilterChanged,
        onApproveBalance,
        onExportAllCLIs,
        onSelectionChanged
    } = props;
    const {state, dispatch, services} = useContext(PeriodChargesContext);
    const cliHasBeatQualifier = CONSTANTS.BEAT_QUALIFIERS.includes(selectedCLI?.beat || "");
    const [hasJeTags, setHasJeTags] = useState(false);
    
    const getValidationError = (validationType: PeriodChargeValidationType) => {
        return validationResult?.validationErrors?.find(error => error.validationType === validationType);
    };

    const renderValidationIcon = (validationType: PeriodChargeValidationType) => {
        if (!validationResult || !hasJeTags) return null;
        
        const error = getValidationError(validationType);
        
        return error ? (
            <Popover
                size="medium"
                position="top"
                triggerType="custom"
                dismissButton={false}
                content={error.errorMessage}
            >
                <Icon name="status-warning" variant="warning" />
            </Popover>
        ) : (
            <Icon name="status-positive" variant="success" />
        );
    };
    
    const columnDefinitions = useMemo(() => {
        const columns = [
            {
                id: "calculationNumber",
                header: "CLI",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) =>
                    'cliCurrentYtdBalance' in e
                        ? e.cliCurrentYtdBalance.calculationNumber
                        : ("JE " + e.jeTagId),
                width: 300,
                minWidth: 180,
                isRowHeader: true
            },
            ...(hasJeTags ? [{
                id: "jeTagActive",
                header: "JE Tag Status",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) => {
                    if ('cliCurrentYtdBalance' in e) return "-";
                    return (e as any).jeTagActive ? "Active" : "InActive";
                },
                width: 120,
                minWidth: 100
            }] : []),
            {
                id: "currency",
                header: "Currency",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) =>
                    'cliCurrentYtdBalance' in e ? e.cliCurrentYtdBalance.calculationCurrency : e.calculationCurrency,
                width: 110,
                minWidth: 110,
            },
            {
                id: "ytdTotalBeginningBalance",
                header: cliHasBeatQualifier ? "YTD total beginning balance" : "YTD beginning balance",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) => {
                    const isCliRow = 'cliCurrentYtdBalance' in e;
                    const balance = isCliRow ? e.cliCurrentYtdBalance.ytdTotalBeginningBalance : e.ytdTotalBeginningBalance;
                    return (
                        <SpaceBetween direction="horizontal" size="xs">
                            {isCliRow && renderValidationIcon(PeriodChargeValidationType.YTD_TOTAL_SUM_MISMATCH)}
                            <Box float="right">{balance == null ? '-' : services.formattingService.formatNumber(balance, true, 2)}</Box>
                        </SpaceBetween>
                    );
                },
                width: cliHasBeatQualifier ? 260 : 240,
                minWidth: 200
            },
            {
                id: "month",
                header: "Month",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) =>
                    'cliCurrentYtdBalance' in e ? e.cliCurrentYtdBalance.month : e.month,
                width: 100,
                minWidth: 60
            },
            {
                id: "year",
                header: "Year",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) =>
                    'cliCurrentYtdBalance' in e ? e.cliCurrentYtdBalance.year : e.year,
                width: 100,
                minWidth: 60
            },
            {
                id: "createUser",
                header: "Updated by",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) =>
                    ('cliCurrentYtdBalance' in e ? e.cliCurrentYtdBalance.createUser : e.createUser) || "-",
                width: 200,
                minWidth: 170
            },
            {
                id: "updatedOn",
                header: "Last update timestamp",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) =>
                    ('cliCurrentYtdBalance' in e ? e.cliCurrentYtdBalance.updatedOn : e.updatedOn) || "-",
                width: 200,
                minWidth: 170
            }
        ];

        if (cliHasBeatQualifier) {
            columns.splice(2, 0, {
                id: "ytdBaseBeginningBalance",
                header: "YTD base beginning balance",
                cell: (e: BalanceAdjustmentYtdTopGridModel | YtdPeriodBalance) => {
                    const isCliRow = 'cliCurrentYtdBalance' in e;
                    const balance = isCliRow ? e.cliCurrentYtdBalance.ytdBaseBeginningBalance : e.ytdBaseBeginningBalance;
                    return (
                        <SpaceBetween direction="horizontal" size="xs">
                            {isCliRow && renderValidationIcon(PeriodChargeValidationType.YTD_BASE_SUM_MISMATCH)}
                            <Box float="right">{balance == null ? '-' : services.formattingService.formatNumber(balance, true, 2)}</Box>
                        </SpaceBetween>
                    );
                },
                width: 240,
                minWidth: 200,
            });
        }
        return columns;
    }, [cliHasBeatQualifier, hasJeTags, validationResult]);

    const {
        searchPeriodChargesIsLoading,
        searchPeriodChargesError,
        periodCharges,
        selectedPeriodCharges,
        exportPeriodChargesFileURL,
        exportPeriodChargesStatus
    } = state;

    const [sortedPeriodCharges, setSortedPeriodCharges] = useState<BalanceAdjustmentYtdTopGridModel[]>([]);

    const [
        selectedFilterAssignee,
        setSelectedFilterAssignee
    ] = useState({value: "All"} as any);

    const [
        selectedStatusFilter,
        setSelectedStatusFilter
    ] = useState({value: "All"} as any);

    const [
        selectedCLIFilter,
        setSelectedCLIFilter
    ] = useState('');

    const [
        selectedYearFilter,
        setSelectedYearFilter
    ] = useState({value: "All"} as any);

    const [newDownloadFlag, setNewDownloadFlag] = useState(null as string | null)

    const [expandedItems, setExpandedItems] = useState([] as any);

    const [templateDownloadUrl, templateUrlLoading, templateUrlError] = services.periodChargesService.downloadTemplate(newDownloadFlag);

    const [jeTags, jeTagsLoading, jeTagsError] = services.calculationStepsService.getJETagsForYTDGrid(selectedCLI?.calculationNumber)

    useEffect(() => {
        if (StringUtils.isNullOrEmpty(exportPeriodChargesFileURL)) {
            return;
        }
        window.open(exportPeriodChargesFileURL);
    }, [exportPeriodChargesFileURL])

    useEffect(() => {
        if (jeTagsError) {
            // Not showing any error message here since error messages are handled by other components.
            return;
        }

        if (jeTags === null || periodCharges.length === 0) {
            return;
        }

        setHasJeTags(jeTags?.JETags && jeTags.JETags.length > 0);

        const sortedJeTagsCurrentYtdBalances = periodCharges[0]?.jeTagsCurrentYtdBalances
            ?.sort((a: { jeTagId: number; }, b: { jeTagId: number; }) => (a?.jeTagId || 0) - (b?.jeTagId || 0))
            ?.map((balance: YtdPeriodBalance) => {
                const jeTag = jeTags?.JETags?.find(tag => tag.id === balance.jeTagId);
                return { ...balance, jeTagActive: jeTag?.jeTagActive };
            }) || [];
        const sortedPeriodCharges = periodCharges.map((periodCharge: BalanceAdjustmentYtdTopGridModel) => {
            return {
                ...periodCharge,
                jeTagsCurrentYtdBalances: sortedJeTagsCurrentYtdBalances
            }
        })

        dispatch(ACTIONS.SET_SELECTED_PERIOD_CHARGES.withPayload(sortedPeriodCharges));
        setSortedPeriodCharges(sortedPeriodCharges);
    }, [jeTags, jeTagsError, periodCharges]);

    useEffect(() => {
        if (filterLOVs == null) {
            return;
        }
        filterLOVs.assignees.unshift("All");
        filterLOVs.years.unshift("All");
        filterLOVs.statuses.unshift("All");
    }, [filterLOVs])

    useEffect(() => {
        if (onFilterChanged == null) {
            return;
        }
        onFilterChanged({
            selectedCLI: selectedCLIFilter,
            selectedAssignee: selectedFilterAssignee?.value,
            selectedStatus: selectedStatusFilter?.value,
            selectedYear: selectedYearFilter?.value
        })
        dispatch(ACTIONS.SET_SELECTED_PERIOD_CHARGES.withPayload([]));
    }, [selectedCLIFilter, selectedFilterAssignee, selectedStatusFilter, selectedYearFilter])

    const onExportAllCLIsClicked = () => {
        if (onExportAllCLIs != null) {
            onExportAllCLIs();
        }
    }

    const onUploadBalancesClicked = () => {
        dispatch(ACTIONS.SET_SHOW_UPLOAD_PERIOD_CHARGES.withPayload(true));
    }

    useEffect(() => {
        if (onSelectionChanged) {
            onSelectionChanged(selectedPeriodCharges);
        }
    }, [selectedPeriodCharges])

    useEffect(() => {
        if (!StringUtils.isNullOrEmpty(templateDownloadUrl)) {
            window.open(templateDownloadUrl as string);
        }
    }, [templateDownloadUrl])

    const renderTableFilters = function () {
        return <Grid
            gridDefinition={[{colspan: 4}, {colspan: 4}, {colspan: 2}]}
        >
            {/* <FormField label="Year">
                <Select selectedOption={selectedYearFilter}
                      onChange={({ detail }) =>
                        setSelectedYearFilter(detail.selectedOption)
                      }
                    options={filterLOVs?.years.map(x => ({label: x, value: x}))}
                ></Select>
            </FormField>
            <FormField label="Assignee">
                <Select selectedOption={selectedFilterAssignee} filteringType="auto"
                    onChange={({ detail }) =>
                        setSelectedFilterAssignee(detail.selectedOption)
                    }
                    options={filterLOVs?.assignees.map(x => ({label: x, value: x}))}
                ></Select>
            </FormField> */}
            <FormField
                // label={<span>&nbsp;</span>}
                label="Type CLI to start search"
                errorText={searchPeriodChargesError}
            >
                <ATPCLIsAutosuggest clis={clisAutoSuggestList} initialValue=''
                                    onCLISelected={(value: string) => setSelectedCLIFilter(value)}
                                    onAutocompleteTextChanged={(value: string) => {
                                    }}/>
            </FormField>
            {/* <FormField label="Status">
                <Select selectedOption={selectedStatusFilter}
                    onChange={({ detail }) =>
                        setSelectedStatusFilter(detail.selectedOption)
                    }
                    options={filterLOVs?.statuses.map(x => ({label: x, value: x}))}
                ></Select>
            </FormField> */}
        </Grid>;
    }

    return <Table
        renderAriaLive={({
                             firstIndex,
                             lastIndex,
                             totalItemsCount
                         }) =>
            `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        expandableRows={{
            getItemChildren: (item: any) =>
                item.jeTagsCurrentYtdBalances || [],
            isItemExpandable: (item: BalanceAdjustmentYtdTopGridModel) =>
                Boolean(item.jeTagsCurrentYtdBalances && item.jeTagsCurrentYtdBalances.length > 0),
            expandedItems: expandedItems,
            onExpandableItemToggle: ({detail}) =>
                setExpandedItems((prev: any[]) => {
                    const next = new Set(prev ?? []);
                    detail.expanded
                        ? next.add(detail.item)
                        : next.delete(detail.item);
                    return [...next];
                })
        }}
        columnDefinitions={columnDefinitions}
        items={sortedPeriodCharges}
        loading={searchPeriodChargesIsLoading}
        loadingText="Loading resources"
        resizableColumns
        empty={
            <Box
                margin={{vertical: "xs"}}
                textAlign="center"
                color="inherit"
            >
                <SpaceBetween size="m">
                    <b>No resources</b>
                </SpaceBetween>
            </Box>
        }
        header={
            <Header
                counter={
                    selectedPeriodCharges.length
                        ? `(${selectedPeriodCharges.length} selected of ${(sortedPeriodCharges.length + (sortedPeriodCharges[0]?.jeTagsCurrentYtdBalances?.length || 0))})`
                        : `(${sortedPeriodCharges.length})`
                }
                actions={
                    <SpaceBetween
                        direction="horizontal"
                        size="xs"
                    >
                        <Button onClick={onExportAllCLIsClicked}
                                disabled={!StringUtils.isNullOrEmpty(exportPeriodChargesStatus)}>{StringUtils.isNullOrEmpty(exportPeriodChargesStatus) ? "Export all CLIs" : exportPeriodChargesStatus}</Button>
                        <Button onClick={onUploadBalancesClicked}>
                            Upload Period Balances
                        </Button>
                        <Button disabled={templateUrlLoading}
                                onClick={() => setNewDownloadFlag(new Date().toISOString())}>Download Period Balance
                            Template</Button>
                    </SpaceBetween>
                }
            >YTD Prior Period Balance</Header>
        }
        pagination={
            <Pagination currentPageIndex={1} pagesCount={1}/>
        }
        selectionType="single"
        selectedItems={selectedPeriodCharges}
        onSelectionChange={({detail}) => dispatch(ACTIONS.SET_SELECTED_PERIOD_CHARGES.withPayload(detail.selectedItems))}
        filter={renderTableFilters()}

    ></Table>
}

function renderApprovalStatus(approvalStatus: string) {

    switch (approvalStatus) {
        case "Pending approval":
            return <StatusIndicator type="pending">{approvalStatus}</StatusIndicator>;
        case "Approved":
            return <StatusIndicator type='success'>{approvalStatus}</StatusIndicator>;
        default:
            return approvalStatus;
    }
}

